import { HealthConditionDetails, MedicationDetails } from '../FleetManagement/types';
import { EnrolmentFormSchema } from './components/EnrolmentForm/validator';

export type DiscoveryResultType = 1 | 2 | 3; // 1 - eligible, 2 - not eligible, 3 - not found

export type InsuranceType =
    | 'ORIGINAL_MEDICARE'
    | 'MEDICARE_ADVANTAGE'
    | 'OTHER_INSURANCE'
    | 'NOT_SURE';

export type ApprovalStatus =
    | 'INITIAL_INTEREST'
    | 'INSURANCE_VALIDATION'
    | 'QUALIFIED'
    | 'MEDICAL_CHECK'
    | 'APPROVED'
    | 'PENDING_ENROLLMENT'
    | 'REJECTED'
    | 'EXPIRED';

export type InitialInterestSubStates =
    | 'PENDING_CALL'
    | 'SCHEDULED_CALL'
    | 'CALL_ATTEMPTED'
    | 'WRONG_NUMBER';

export type InsuranceValidationSubStates = 'NEED_TO_VALIDATE' | 'VALIDATING';

export type MedicalCheckSubStates =
    | 'IMPENDING_APPOINTMENT'
    | 'MISSED_APPOINTMENT'
    | 'CALL_ATTEMPTED';

export type QualifiedSubStates = 'CONSENT_NEEDED' | 'SCHEDULED_CALL' | 'CALL_ATTEMPTED';

export type ApprovedSubStates = 'ENROLLED' | 'ENROLMENT_NEEDED';

export type RejectedSubStates =
    | 'NO_INSURANCE'
    | 'DUPLICATE'
    | 'STALE'
    | 'NOT_INTERESTED'
    | 'NO_NECESSITY';

export type ExpiredSubStates = 'EXPIRED';

export type AllApprovalStatusSubStates =
    | InitialInterestSubStates
    | MedicalCheckSubStates
    | InsuranceValidationSubStates
    | QualifiedSubStates
    | RejectedSubStates
    | ApprovedSubStates
    | ExpiredSubStates;

export type AddressValidationStatus = 'validated' | 'unvalidated';

export type HeroFor = 'MYSELF' | 'LOVED_ONE';

export type LeadData = {
    id: number;
    lead_id: string;
    org_lead_id: string | null;
    hero_organization_id: string;
    hero_user_id?: number;
    member_id: number | null;
    submitter_hero_user_id: number | null;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string;
    state: string;
    date_of_birth: string;
    street_address: string | null;
    apartment: string | null;
    city: string | null;
    zip: string | null;
    shipping_zip: string | null;
    caregiver_first_name: string | null; // if caregiver role
    caregiver_last_name: string | null;
    caregiver_email?: string | null;
    caregiver_phone: string | null;
    admin_email: string | null;
    external_serial: string | null;
    // The list endpoint returns the numeric DB id; the detail endpoint
    // (`LeadDetailsResponse.primary_user_details`) returns the `InsuranceType` string.
    insurance_type: number | null;
    insurance_carrier_by_user: string | null;
    insurance_policy_id: string | null;
    medigap_insurance_name: string | null;
    medigap_insurance_policy_id: string | null;
    medicare_card_image_key: string | null;
    approval_status: ApprovalStatus;
    approval_sub_status: AllApprovalStatusSubStates | null;
    // Tri-state: null means the member question was never answered on the form.
    is_member: boolean | null;
    is_new?: boolean;
    is_duplicate: boolean;
    is_tcpa_opt_in: boolean | null;
    is_forbidden_rtm_election: boolean | null;
    agreed_terms: boolean | null;
    signed_consent_uploaded: boolean;
    completed: boolean;
    created_by: number | null;
    previous_member_id: number | null;
    created_at: string;
    completed_at: string | null;
    external_user_id: string | null;
    reactivated_at: string | null;
    expires_at: string | null;
    expiration_flags: number;
    deleted: boolean;
    discovery_result: DiscoveryResultType | null;
    experience_flow: number | null;
    is_exported: boolean;
    exported_timestamp: string | null;
    hero_for: HeroFor;
    status_changed_timestamp: string;
    change_source: string | null;
    updated_at: string | null;
    utm_campaign: string | null;
    utm_content: string | null;
    utm_medium: string | null;
    utm_source: string | null;
    utm_term: string | null;
    affiliate_source: string | null;
    form_version: string;
    membership_request_id: number | null;
    patient_stage: string | null;
    status_change_note: string | null;
    status_change_note_date_time: string | null;
    lock_status: 'not_locked' | 'locked';
    claimed_by: string | null;
    claim_expires_at: string | null;
    has_insurance_discovery: boolean;
    // Redesign fields (HWM-22633)
    priority_tier?: number;
    sla_text?: string | null;
    next_action?: string | null;
    sla_postponed_until: string | null;
    sla_postpone_reason: string | null;
    address_validation_status?: AddressValidationStatus | null;
    // Legacy boolean kept by rtm-api; `address_validation_status` is authoritative.
    address_verified: boolean | null;
    communication_count?: number;
    // HWM-22633: Per-channel counts (replaces single aggregate in the UI).
    // Backend still emits `communication_count` for backwards-compat.
    sms_count?: number;
    call_count?: number;
    email_count?: number;
    unread_sms_count?: number;
    has_notes?: boolean;
    // HWM-22633 round 3: most-recent non-deleted note on the lead. Powers the
    // single-tap edit/add modal on the list page without a per-row fetch.
    latest_note?: { id: number; body: string; updated_at: string } | null;
    // Per-provider gate for SMS/Email, mirrors LeadDetailsResponse. Lead-list
    // trigger buttons in the Communication column must hide when false.
    messaging_enabled?: boolean;
    // HWM-22633: sub-status-aware display label from the backend.
    // (MEDICAL_CHECK, IMPENDING_APPOINTMENT) -> "Medical check scheduled".
    // (MEDICAL_CHECK, MISSED_APPOINTMENT)  -> "Medical check missed".
    status_display_label?: string;
};

export type LeadDataResponse = {
    leads: LeadData[];
    limit: number;
    offset: number;
    total_count: number;
};

export type StatesFilter = {
    approval_status: ApprovalStatus;
};

/**
 * Sort fields the leads list endpoint accepts. Runtime tuple rather than a bare union so
 * `useQueryParams` can reject a hand-edited `?sort_by=` before it reaches the backend, and
 * `columns.ts` can only mark a column sortable by a field the API actually supports.
 */
export const LEAD_SORT_FIELDS = ['created_at', 'updated_at', 'completed_at', 'expires_at'] as const;

export type LeadSortField = (typeof LEAD_SORT_FIELDS)[number];

export type LeadSortDir = 'ASC' | 'DESC';

export type LeadQueryParams = {
    filter_type?: 'default' | 'expiring_leads';
    search?: string;
    is_member?: number;
    is_new?: boolean;
    created_from?: string;
    created_to?: string;
    updated_from?: string;
    updated_to?: string;
    states?: StatesFilter[];
    state?: string[];
    offset?: number;
    limit?: number;
    sort_by?: LeadSortField;
    sort_dir?: LeadSortDir;
};

export type LeadQueryExport = LeadQueryParams;

export type UpdateLeadPayload = {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    state?: string;
    caregiver_first_name?: string;
    caregiver_last_name?: string;
    caregiver_phone?: string;
    caregiver_email?: string;
    street_address?: string;
    apartment?: string | null;
    city?: string;
    shipping_zip?: string;
    admin_email?: string;
    external_serial?: string;
    is_member?: boolean;
    ignore_org_allowed_transfer?: boolean;
    insurance_type?: string;
    insurance_carrier_by_user?: string | null;
    insurance_policy_id?: string;
    medigap_insurance_name?: string | null;
    medigap_insurance_policy_id?: string | null;
};

export type LeadEnrolmentPayload = {
    lead_id: string;
    approval_sub_status?: AllApprovalStatusSubStates;
    approval_status?: ApprovalStatus;
    is_member?: boolean;
    admin_email?: string;
    external_serial?: string;
    aux_data?: Pick<EnrolmentFormSchema, 'email' | 'phone'> & {
        shipping_address: {
            address_line_1: string;
            address_line_2?: string;
            city?: string;
            zip_code?: string;
            state?: string;
        };
    };
};

export type LeadCreatePayload = {
    hero_for: string;
    is_member?: boolean;
    anonymous_id: string;
    form_version: string;
    insurance_type: string;
    admin_email?: string | null;
    external_serial?: string | null;
    insurance_carrier_by_user?: string | null;
    insurance_policy_id?: string | null;
    medigap_insurance_name?: string | null;
    medigap_insurance_policy_id?: string | null;
    patient_information: {
        first_name: string;
        last_name: string;
        date_of_birth: string;
        zip: string;
        phone: string | null;
        email: string | null;
    };
    caregiver_information: {
        first_name: string | null | undefined;
        last_name: string | null | undefined;
        email: string | null | undefined;
        phone: string | null | undefined;
    };
};

export type LeadStatesResponse = {
    new_application: InitialInterestSubStates[];
    pending_manual_vob: InsuranceValidationSubStates[];
    qualified: QualifiedSubStates[];
    medical_check: MedicalCheckSubStates[];
    approved: ApprovedSubStates[];
    rejected: RejectedSubStates[];
};

export type LeadStateResponse = {
    current_state: string;
    possible_state_transitions?: string[];
    states_requiring_note: string[];
    states_requiring_date_time: string[];
    states_requiring_sub_state: {
        state: string;
        sub_state_transitions_for_state: string[];
        sub_states_requiring_note: string[];
        sub_states_requiring_note_date_time: string[];
    }[];
    possible_sub_state_transitions_for_current_state?: string[];
    sub_states_requiring_note: string[];
    sub_states_requiring_note_date_time: string[];
};

export type UpdateLeadApprovalStatusPayload = {
    lead_id: string; // mandatory
    approval_status?: ApprovalStatus; // Optional
    approval_sub_status?: AllApprovalStatusSubStates; // Optional
    note?: string; // Optional
    note_date_time?: string; // Optional
    aux_data?: {
        shipping_address: {
            address_line_1: string;
            address_line_2?: string;
            city?: string;
            zip_code?: string;
            state?: string;
        };
    };
};

export type LeadActivityDetailStatusChange = {
    entry_type: 'status_change';
    status: ApprovalStatus;
    sub_status: AllApprovalStatusSubStates | null;
    updated_by: string | null;
    updated_at: string;
    note: string | null;
    note_datetime: string | null;
    // Non-human attribution for a status change (HWM-22751). `"drchrono"` for
    // DrChrono webhook-driven flips. `null`/absent for manual portal actions —
    // fall back to rendering `updated_by`. Forward-compat: unrecognized
    // strings render as `updated_by` too (see LeadActivity.tsx).
    source?: string | null;
};

export type LeadNote = {
    id: number;
    lead_id: string;
    note: string;
    created_by: string | null;
    created_at: string;
    updated_at: string | null;
    deleted?: boolean;
};

export type LeadActivityDetailNote = {
    entry_type: 'note';
    status: ApprovalStatus;
    sub_status: AllApprovalStatusSubStates | null;
    updated_by: string | null;
    updated_at: string;
    note: string | null;
    note_datetime: string | null;
    note_id?: number;
};

// HWM-22633: SRS specifies exactly three call-disposition outcomes:
// Connected / No answer / Left voicemail. The pre-redesign set
// (answered / busy / wrong_number / voicemail) is gone.
export type CallDispositionType = 'connected' | 'no_answer' | 'left_voicemail';

export type CallDisposition = {
    id: number;
    lead_id: string;
    disposition: CallDispositionType;
    notes: string | null;
    created_by: string | null;
    created_at: string;
};

export type LeadActivityDetailCallDisposition = {
    entry_type: 'call_disposition';
    status: ApprovalStatus;
    sub_status: AllApprovalStatusSubStates | null;
    updated_by: string | null;
    updated_at: string;
    note: string | null;
    note_datetime: string | null;
    disposition?: CallDispositionType;
};

export type LeadActivityDetailSlaPostpone = {
    entry_type: 'sla_postpone';
    status: ApprovalStatus;
    sub_status: AllApprovalStatusSubStates | null;
    updated_by: string | null;
    updated_at: string;
    note: string | null;
    note_datetime: string | null;
    postponed_until?: string;
};

// Union — backwards compat: entries without entry_type are treated as status_change
export type LeadActivityDetail =
    | LeadActivityDetailStatusChange
    | LeadActivityDetailNote
    | LeadActivityDetailCallDisposition
    | LeadActivityDetailSlaPostpone
    | {
          entry_type?: undefined;
          status: ApprovalStatus;
          sub_status: AllApprovalStatusSubStates | null;
          updated_by: string | null;
          updated_at: string;
          note: string | null;
          note_datetime: string | null;
      };

export interface LeadInsuranceDetails {
    name: string;
    date_of_birth: string;
    gender: string;
    address: string;
    zip: string;
    state: string;
    city: string;
    policy_type: string;
    payer: string;
    member_id: string;
    main_member_id: string;
    main_payer_name: string;
    effective_date: string;
    plan_name: string;
    plan_number: string;
    group_number: string;
    group_name: string;
    plan_network_id: string;
    plan_network_name: string;
    status: string;
}

// Activity log entries (HWM-22633) — discriminated union mirroring rtm-api
// helpers in enterprise/lead_activity_log.py
export type ActivityLogEntry =
    | {
          kind: 'status_change';
          // Historical PatientLead row PK. Targets
          // PATCH /enterprise/leads/<lead_id>/status-changes/<id>/note/.
          id: number;
          created_at: string;
          from_status?: string | null;
          to_status?: string | null;
          from_sub_status?: string | null;
          to_sub_status?: string | null;
          // HWM-22633 round 4: editable per-status agent annotation.
          note?: string | null;
          note_date_time?: string | null;
          // HWM-22633 round 4b: agent who performed the status change —
          // needed by the legacy `LeadActivity` per-row visual.
          updated_by?: string | null;
      }
    | {
          kind: 'sms';
          id: number;
          created_at: string;
          body: string | null;
          direction: 'inbound' | 'outbound';
          sms_type?: string | null;
          sent_by?: number | null;
          read_at?: string | null;
      }
    | {
          kind: 'email';
          id: number;
          created_at: string;
          subject?: string | null;
          body: string | null;
          direction: 'inbound' | 'outbound';
          email_type?: string | null;
          sent_by?: number | null;
      }
    | {
          kind: 'call_disposition';
          id: number;
          created_at: string;
          outcome: string;
          note?: string | null;
          created_by?: number | null;
      }
    | {
          kind: 'note';
          id: number;
          created_at: string;
          body: string;
          created_by?: number | null;
      };

export type LeadDetailsResponse = {
    status: ApprovalStatus;
    sub_status: AllApprovalStatusSubStates;
    claimed_by: string | null;
    lock_expires: string | null;
    lock_status: string | null;
    claimed_by_id: number | null;
    form_version: string | null;
    is_existing_member?: boolean;
    is_forbidden_rtm_election?: boolean;
    // Redesign fields (HWM-22633)
    sla_postponed_until?: string | null;
    sla_postpone_reason?: string | null;
    sla_text?: string | null;
    address_validation_status?: AddressValidationStatus | null;
    // HWM-22633: canonical "last status change" timestamp from the BE
    // (ISO-8601 with UTC offset). Both DrChrono booking and the manual
    // NewActivityForm flow stamp the medical-check appointment slot here.
    // FE derives `medical_check_scheduled_at` via `getMedicalCheckScheduledAt`
    // in `utils/medicalCheckStatus.ts`.
    status_change_note_date_time?: string | null;
    // Per-provider gate for SMS/Email. Backend omits this if the lead's
    // provider has not been onboarded; treat undefined as "not enabled".
    messaging_enabled?: boolean;
    // HWM-22633: "drchrono" when the lead's org has a DrChrono config,
    // else "manual". Portal branches the Medical-check scheduling UX
    // on this — Polycare gets the dr-chrono widget; every other provider
    // gets a manual date/time/note form.
    scheduling_provider?: 'drchrono' | 'manual' | null;
    primary_user_details: {
        first_name: string | null;
        last_name: string | null;
        lead_id: string | null;
        created_date: string | null;
        completed_date: string | null;
        date_of_birth: string | null;
        insurance_company: string | null;
        insurance_id: string | null;
        insurance_type: string | null;
        phone: string | null;
        email: string | null;
        zip: string | null;
        admin_email: string | null;
        external_serial: string | null;
        external_user_id?: string | null;
        signed_consent_uploaded: boolean;
        medigap_insurance_name?: string | null;
        medigap_insurance_policy_id?: string | null;
        previous_member_id: number | null;
        reactivated_date: string | null;
        expires_at: string | null;
        insurances: LeadInsuranceDetails[] | null;
        medicare_card_image_url?: string | null;
        medicare_card_download_url?: string | null;
    };
    contact_details: {
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        phone: string | null;
    };
    address_details: {
        street_address: string | null;
        apartment: string | null;
        city: string | null;
        state: string | null;
        shipping_zip: string | null;
    };
    activities_details: LeadActivityDetail[];
    // HWM-22633: merged activity log from rtm-api (LeadSmsLog + LeadEmailLog +
    // CallDisposition + LeadNote, sorted by created_at desc, kind-discriminated).
    // When present this is the authoritative source; activities_details is
    // retained for backwards compatibility.
    activity_log?: ActivityLogEntry[];
    // HWM-22633 round 4: most-recent non-deleted LeadNote on this lead.
    // Mirrors the list-row `latest_note` shape so the StickyNotePanel
    // (Activities-tab right column) reads one shape across views.
    latest_note?: { id: number; body: string; updated_at: string } | null;
};

// HWM-22633 round 4: canonical LeadNote payload returned by every mutation
// + the list-row aggregate. Used to patch React Query cache without a
// refetch.
export type LeadNotePayload = { id: number; body: string; updated_at: string };

// HWM-22633 round 4: response shape for the new per-status note endpoint.
export type StatusChangeNotePayload = {
    id: number;
    note: string | null;
    note_date_time: string | null;
    updated_at: string | null;
};

export type LeadInsuranceResponse = {
    name: string;
    type: string;
    supports_multiple_carriers: boolean;
}[];

export type LeadConsentResponse = {
    download_url: string;
};

export type LeadHealthAssessmentResponse = {
    pills: MedicationDetails[];
    health_conditions: HealthConditionDetails[] | null;
};

// New API payload types (HWM-22633)

// HWM-22633 round 7: address_confirmation retired (not in SRS).
export type SmsType = 'free_form' | 'medical_check' | 'reschedule' | 'address';

export type SendSmsPayload = {
    lead_id: string;
    sms_type: SmsType;
    message_body?: string;
};

// HWM-22633 round 7: free-form email retired. Only the canned Medical
// Check template exists; rtm-api hardcodes email_type="medical_check"
// server-side, so the request body is just the lead id.
export type SendEmailPayload = {
    lead_id: string;
};

export type CallDispositionPayload = {
    lead_id: string;
    outcome: CallDispositionType;
    note?: string;
};

export type PostponeSlaPayload = {
    lead_id: string;
    postpone_until: string;
    reason?: string;
};

export type CreateLeadNotePayload = {
    lead_id: string;
    body: string;
};

export type UpdateLeadNotePayload = {
    body?: string;
    deleted_at?: string | null;
};

export type UnreadSmsItem = {
    id: number;
    lead_id: string;
    lead_first_name: string;
    lead_phone?: string | null;
    provider_id: number;
    provider_name: string;
    direction: 'inbound' | 'outbound';
    body: string | null;
    sms_type: string | null;
    sent_by: number | null;
    sent_by_name: string | null;
    read_at: string | null;
    delivery_status: string;
    created_at: string;
};

export type UnreadSmsCountResponse = { count: number };

export type ValidateAddressPayload = {
    lead_id: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    zip?: string;
    // `manual_validate` — validate the lead's existing typed address without
    // requiring a Google Places autocomplete pick. Backend runs structural
    // validation and flips status to validated when it passes.
    address_source?: 'google_places' | 'manual' | 'manual_validate';
};
